import { shareCopy } from '#src/app/shared/share/copy';
import { shareEmail } from '#src/app/shared/share/email';
import { buildFacebookTrackingEvents, shareFacebook } from '#src/app/shared/share/facebook';
import { shareFacebookMessenger } from '#src/app/shared/share/facebookMessenger';
import { shareLine } from '#src/app/shared/share/line';
import { shareNative } from '#src/app/shared/share/mobileNativeShare';
import { shareNextdoor } from '#src/app/shared/share/nextdoor';
import { shareReddit } from '#src/app/shared/share/reddit';
import { shareSms } from '#src/app/shared/share/sms';
import { shareTwitter } from '#src/app/shared/share/twitter';
import { getNavigatorShareInterface } from '#src/app/shared/share/utils';
import { shareVk } from '#src/app/shared/share/vk';
import { shareWhatsapp } from '#src/app/shared/share/whatsapp';

import type { ShareChannel } from '../../../shared/types';

import { InstagramShare } from './instagram';
import { InstagramStoriesShare } from './instagramStories';
import { TiktokShare } from './tiktok';
import { buildTrackingEvents } from './trackingEvents';
import type { ShareActionConfig } from './types';

const SHARE_ACTIONS: Readonly<Record<ShareChannel, ShareActionConfig>> = {
	facebook: {
		type: 'function',
		action: async ({ url, trackingData, trackingEventName, config: { facebook } }, { utilityContext, track }) =>
			shareFacebook(
				url,
				{
					trackingData,
					trackingEvents: buildFacebookTrackingEvents(trackingEventName),
					mode: facebook?.mode,
				},
				utilityContext,
				track,
			),
	},
	facebookMessenger: {
		type: 'function',
		action: async ({ url, trackingData, trackingEventName }, { utilityContext, track }) => {
			shareFacebookMessenger(
				url,
				{
					trackingData,
					trackingEvents: buildTrackingEvents(trackingEventName),
				},
				utilityContext,
				track,
			);
			return Promise.resolve(true);
		},
	},
	twitter: {
		type: 'function',
		action: async (
			{ url, content: { title }, trackingData, trackingEventName },
			{ track, utilityContext, countryCode, locale },
		) => {
			shareTwitter({
				url,
				comment: title,
				options: { trackingData, trackingEvents: buildTrackingEvents(trackingEventName), countryCode, locale },
				track,
				utilityContext,
			});
			return Promise.resolve(true);
		},
	},
	whatsapp: {
		type: 'function',
		action: async ({ url, content: { title }, trackingData, trackingEventName }, { track }) => {
			shareWhatsapp(
				title,
				url,
				{
					trackingData,
					trackingEvents: buildTrackingEvents(trackingEventName),
				},
				track,
			);
			return Promise.resolve(true);
		},
	},
	reddit: {
		type: 'function',
		action: async ({ url, content: { title }, trackingData, trackingEventName }, { utilityContext, track }) => {
			shareReddit(
				title,
				url,
				{
					trackingData,
					trackingEvents: buildTrackingEvents(trackingEventName),
				},
				track,
				utilityContext.i18n,
			);
			return Promise.resolve(true);
		},
	},
	vk: {
		type: 'function',
		action: async ({ url, content: { title }, trackingData, trackingEventName }, { track }) => {
			shareVk(
				url,
				title,
				{
					trackingData,
					trackingEvents: buildTrackingEvents(trackingEventName),
				},
				track,
			);
			return Promise.resolve(true);
		},
	},
	line: {
		type: 'function',
		action: async ({ url, content: { title }, trackingData, trackingEventName }, { utilityContext, track }) => {
			shareLine(
				url,
				title,
				{
					trackingData,
					trackingEvents: buildTrackingEvents(trackingEventName),
				},
				track,
				utilityContext.i18n,
			);
			return Promise.resolve(true);
		},
	},
	email: {
		type: 'function',
		action: async ({ content: { title, body }, trackingData, trackingEventName }, { track }) => {
			shareEmail(
				title,
				body,
				{
					trackingData,
					trackingEvents: buildTrackingEvents(trackingEventName),
				},
				track,
			);
			return Promise.resolve(true);
		},
	},
	sms: {
		type: 'function',
		action: async ({ url, content: { title }, trackingData, trackingEventName }, { utilityContext, track }) => {
			shareSms(
				url,
				title,
				{
					trackingData,
					trackingEvents: buildTrackingEvents(trackingEventName),
				},
				utilityContext,
				track,
			);
			return Promise.resolve(true);
		},
	},
	instagram: {
		type: 'component',
		Component: InstagramShare,
	},
	instagramStories: {
		type: 'component',
		Component: InstagramStoriesShare,
	},
	tiktok: {
		type: 'component',
		Component: TiktokShare,
	},
	nextdoor: {
		type: 'function',
		action: async ({ url, content: { title }, trackingData, trackingEventName }, { track }) => {
			shareNextdoor(
				url,
				title,
				{
					trackingData,
					trackingEvents: buildTrackingEvents(trackingEventName),
				},
				track,
			);
			return Promise.resolve(true);
		},
	},
	copy: {
		type: 'function',
		action: async ({ url, trackingData, trackingEventName }, { utilityContext, track }) =>
			shareCopy(
				url,
				{
					trackingData,
					trackingEvents: buildTrackingEvents(trackingEventName, { failure: true }),
				},
				track,
				utilityContext,
			),
	},
	mobileNativeShare: {
		type: 'function',
		action: async (
			{ url, trackingData, trackingEventName, config: { mobileNativeShare } },
			{ utilityContext, track },
		) => {
			const title = mobileNativeShare?.title;
			const data = title ? { url, title } : { url };
			// The same check `shareNative` makes before opening the sheet. A browser that refuses this payload
			// never shows the sheet, so the tap neither enrolls nor stamps the arm on its intent event.
			const sheetOpens = !!getNavigatorShareInterface()?.canShare(data);
			if (sheetOpens) mobileNativeShare?.onShareTap?.();
			return shareNative(
				data,
				{
					trackingData: sheetOpens ? { ...trackingData, ...mobileNativeShare?.trackingData } : trackingData,
					trackingEvents: buildTrackingEvents(trackingEventName, {
						intent: true,
						cancel: true,
						failure: true,
					}),
				},
				track,
				utilityContext,
			);
		},
	},
};

export function getShareActionConfig(channel: ShareChannel): ShareActionConfig {
	return SHARE_ACTIONS[channel];
}
